import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { ScheduleService } from 'src/app/services/schedule.service';

export interface HourItem {
  id: number;
  from: string;
  to: string;
  sta_id?: number;
  sta_name: string;       // 'ACTIVA', 'CONFIRMADA', etc.
  is_ondemand: boolean;
  use_name?: string | null;
  use_paternal_surname?: string | null;
}

export interface CalendarCell {
  date: Date;
  key: string;            // yyyymmdd
  selected: boolean;
  hasHours: boolean;      // tiene horas disponibles en ese día (ACTIVA y no on-demand y no pasado)
}

@Component({
  selector: 'app-schedule-picker',
  templateUrl: './schedule-picker.component.html',
  styleUrls: ['./schedule-picker.component.css']
})
export class SchedulePickerComponent implements OnInit, OnChanges {

  @Input() docId: number | null = null;

  // opcional: si el padre quiere forzar fecha inicial (YYYY-MM-DD)
  @Input() initialDate: string | null = null;

  // opcional: si el padre quiere forzar hora inicial (id)
  @Input() initialHourId: number | null = null;

  @Output() dateSelected = new EventEmitter<string>(); // YYYY-MM-DD
  @Output() slotSelected = new EventEmitter<any>();

  // UI calendario
  dayNames = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

  currentMonth: Date = new Date(); // siempre al 1 del mes
  weeks: (CalendarCell | null)[][] = [];

  selectedKey: string | null = null;

  // horas
  horasDoctor: any[] = [];          // raw desde backend
  selectedHours: HourItem[] = [];
  selectedHourId: number | null = null;

  constructor(private scheduleService: ScheduleService) {}

  // =========================================================
  // LIFECYCLE
  // =========================================================
  ngOnInit(): void {
    // ✅ Siempre partir en el mes actual y con HOY seleccionado
    const today = this.todayLocal();
    this.currentMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    this.selectedKey = this.keyFromDate(today);
    this.selectedHours = [];

    this.buildCalendar();

    // Si llega docId ya seteado
    if (this.docId != null) {
      this.getHorasDoctor(Number(this.docId));
    } else {
      // aun así dejamos hoy seleccionado (sin horas)
      this.applyInitialSelectionOrAutoselect();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('docId' in changes) {
      const curr = changes['docId'].currentValue;
      if (curr != null) this.getHorasDoctor(Number(curr));
    }

    if ('initialDate' in changes && this.horasDoctor.length) {
      this.applyInitialSelectionOrAutoselect();
    }
  }

  // =========================================================
  // DATA
  // =========================================================
  private getHorasDoctor(id: number) {
    // Ajusta esta llamada si tu service usa otro método/params
    this.scheduleService
      .getScheduleHoursDoc(id, null, true, true)
      .then((data: any[]) => {
        this.horasDoctor = Array.isArray(data) ? data : [];
        this.rebuildDaysWithHours();
        this.buildCalendar();
        this.applyInitialSelectionOrAutoselect();
      })
      .catch((e: any) => {
        console.error(e);
        this.horasDoctor = [];
        this.rebuildDaysWithHours();
        this.buildCalendar();
        this.applyInitialSelectionOrAutoselect();
      });
  }

  // =========================================================
  // CALENDAR NAV
  // =========================================================
  prevMonth(): void {
    this.currentMonth = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() - 1, 1);
    this.buildCalendar();
    this.markSelectedInCalendar(this.selectedKey);
  }

  nextMonth(): void {
    this.currentMonth = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() + 1, 1);
    this.buildCalendar();
    this.markSelectedInCalendar(this.selectedKey);
  }

  monthLabel(): string {
    const meses = [
      'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
      'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
    ];
    return `${meses[this.currentMonth.getMonth()]} ${this.currentMonth.getFullYear()}`;
  }

  // =========================================================
  // SELECCIÓN FECHA/HORA
  // =========================================================
  selectDate(cell: CalendarCell): void {
    if (!cell) return;

    // ✅ bloquear días anteriores a hoy
    if (this.isPastDay(cell.date)) return;

    this.selectedKey = cell.key;
    this.markSelectedInCalendar(cell.key);

    this.selectedHours = this.getHoursForKey(cell.key);
    this.selectedHourId = null;

    this.dateSelected.emit(this.ymdFromKey(cell.key));
  }

  selectHour(hour: HourItem): void {
    if (!hour) return;

    const selectedDate = this.selectedKey ? this.dateFromKey(this.selectedKey) : null;

    // ✅ no permitir seleccionar si está bloqueada
    if (this.isHourDisabled(hour, selectedDate)) return;

    this.selectedHourId = Number(hour.id);

    this.slotSelected.emit({
      id: hour.id,
      from: hour.from,
      to: hour.to,
      is_ondemand: hour.is_ondemand,
      sta_id: hour.sta_id,
      sta_name: hour.sta_name
    });
  }

  // =========================================================
  // REGLAS NUEVAS: hoy seleccionado + pasados bloqueados
  // =========================================================
  private applyInitialSelectionOrAutoselect(): void {
    const targetDate = this.initialDate
      ? (this.parseYmdToLocalDate(this.initialDate) || this.todayLocal())
      : this.todayLocal();

    // Ajustar el mes visible al del target
    this.currentMonth = new Date(targetDate.getFullYear(), targetDate.getMonth(), 1);

    this.rebuildDaysWithHours();
    this.buildCalendar();

    // ✅ Seleccionar la fecha (aunque no tenga horas)
    const key = this.keyFromDate(targetDate);
    this.selectedKey = key;
    this.markSelectedInCalendar(key);

    this.selectedHours = this.getHoursForKey(key);
    this.selectedHourId = null;

    this.dateSelected.emit(this.ymdFromKey(key));

    // ✅ si venía initialHourId, intentar seleccionarla si no está bloqueada
    if (this.initialHourId != null) {
      const wanted = Number(this.initialHourId);
      const found = this.selectedHours.find(h => Number(h.id) === wanted);

      if (found && !this.isHourDisabled(found, targetDate)) {
        this.selectedHourId = Number(found.id);
        this.slotSelected.emit({
          id: found.id,
          from: found.from,
          to: found.to,
          is_ondemand: found.is_ondemand,
          sta_id: found.sta_id,
          sta_name: found.sta_name
        });
      }
    }
  }

  isPastDay(date: Date): boolean {
    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    return d.getTime() < this.todayLocal().getTime();
  }

  private isHourDisabled(hour: HourItem, selectedDate: Date | null): boolean {
    // reglas actuales
    if (hour.is_ondemand) return true;
    if (hour.sta_name !== 'ACTIVA') return true;

    // ✅ nueva regla: si el día es pasado, bloquear aunque esté ACTIVA
    if (selectedDate && this.isPastDay(selectedDate)) return true;

    return false;
  }

  private markSelectedInCalendar(key: string | null) {
    if (!key) return;
    for (const week of this.weeks) {
      for (const cell of week) {
        if (cell) cell.selected = (cell.key === key);
      }
    }
  }

  // =========================================================
  // BUILD CALENDAR
  // =========================================================
  private buildCalendar(): void {
    const year = this.currentMonth.getFullYear();
    const month = this.currentMonth.getMonth();

    const firstDay = new Date(year, month, 1);
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    // Queremos semana empezando lunes:
    // JS getDay(): 0=Dom..6=Sáb
    // convertimos a offset lunes=0..domingo=6
    const firstDow = firstDay.getDay(); // 0..6
    const offset = (firstDow + 6) % 7;  // lunes=0

    const cells: (CalendarCell | null)[] = [];

    // placeholders antes del día 1
    for (let i = 0; i < offset; i++) cells.push(null);

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const key = this.keyFromDate(date);

      const hasHours = this.dayHasAvailableHours(key, date);

      cells.push({
        date,
        key,
        selected: this.selectedKey === key,
        hasHours
      });
    }

    // completar hasta múltiplo de 7
    while (cells.length % 7 !== 0) cells.push(null);

    // partir en semanas
    const weeks: (CalendarCell | null)[][] = [];
    for (let i = 0; i < cells.length; i += 7) {
      weeks.push(cells.slice(i, i + 7));
    }

    this.weeks = weeks;
  }

  private rebuildDaysWithHours(): void {
    // No hace falta precomputar si dayHasAvailableHours ya consulta directo,
    // pero lo dejamos por si después quieres optimizar.
  }

  private dayHasAvailableHours(key: string, date: Date): boolean {
    // ✅ si es pasado: no lo marcamos como disponible
    if (this.isPastDay(date)) return false;

    const hours = this.getHoursForKey(key);

    // disponible = ACTIVA && !onDemand
    return hours.some(h => h.sta_name === 'ACTIVA' && !h.is_ondemand);
  }

  // =========================================================
  // HOURS BY DAY (tolerante a formatos)
  // =========================================================
  private getHoursForKey(key: string): HourItem[] {
    if (!this.horasDoctor?.length) return [];

    const dayObj = this.horasDoctor.find((x: any) => {
      if (!x) return false;

      // Caso A: x.sch_date viene 'YYYY-MM-DD'
      if (typeof x.sch_date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(x.sch_date)) {
        return this.keyFromDate(this.parseYmdToLocalDate(x.sch_date)!) === key;
      }

      // Caso B: x.sch_date viene Date/string parseable
      if (x.sch_date) {
        const d = new Date(x.sch_date);
        if (!isNaN(d.getTime())) return this.keyFromDate(d) === key;
      }

      // Caso C: x.date en vez de sch_date
      if (x.date) {
        const d = new Date(x.date);
        if (!isNaN(d.getTime())) return this.keyFromDate(d) === key;
      }

      return false;
    });

    const rawHours = Array.isArray(dayObj?.hours) ? dayObj.hours : (Array.isArray(dayObj?.data) ? dayObj.data : []);

    const mapped: HourItem[] = (rawHours || []).map((h: any) => ({
      id: Number(h.id ?? h.sch_hou_id ?? 0),
      from: h.from ?? h.sch_from ?? '',
      to: h.to ?? h.sch_to ?? '',
      sta_id: h.sta_id != null ? Number(h.sta_id) : undefined,
      sta_name: h.sta_name ?? h.estado ?? '',
      is_ondemand: !!h.is_ondemand,
      use_name: h.use_name ?? null,
      use_paternal_surname: h.use_paternal_surname ?? null
    })).filter((h: HourItem) => !!h.id && !!h.from && !!h.to && !!h.sta_name);

    mapped.sort((a, b) => (a.from || '').localeCompare(b.from || ''));

    return mapped;
  }

  // =========================================================
  // DATE HELPERS
  // =========================================================
  private todayLocal(): Date {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }

  private keyFromDate(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}${m}${day}`;
  }

  private ymdFromKey(key: string): string {
    return `${key.slice(0, 4)}-${key.slice(4, 6)}-${key.slice(6, 8)}`;
  }

  private dateFromKey(key: string): Date {
    const y = Number(key.slice(0, 4));
    const m = Number(key.slice(4, 6)) - 1;
    const d = Number(key.slice(6, 8));
    return new Date(y, m, d);
  }

  private parseYmdToLocalDate(ymd: string): Date | null {
    if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return null;
    const [y, m, d] = ymd.split('-').map(n => Number(n));
    return new Date(y, m - 1, d);
  }
}
